import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
  type Dispatch,
  type SetStateAction,
} from 'react';

import { useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useAdvisorPrefillPolicy } from '@/shared/hooks/use-per-client-invite-link';
import { parseCurrencyInput } from '../hooks/validate-prefill-draft';

import _ from 'lodash';
import { toast } from 'sonner';
import { v4 as newUuid } from 'uuid';
import { analytics } from '@client/analytics';
import { inviteApi } from '../api/api';
import {
  selectSessionAdvisor,
  selectFeatureFlags,
  fetchClientsForAdvisorByAdvisorIdRequest,
} from '@client/model';
import {
  getIntroIntakeFormApiEndPoint,
  getClientOnboardingUrl,
  isAdvisorPrefillQuestionAllowed,
  isValidEmailFormat,
  fullName,
  isConsumerSmsMfaEnabled,
} from '@shared';
import type { AdvisorPrefillPolicy } from '@shared';
import {
  stripDigits,
  validateForCountry,
  type SupportedCountryCode,
} from '@client/util/clerkAuthHelpers';

export type TabType = 'email' | 'link' | 'fill-form' | 'self-test';
export type StepType =
  | 'method'
  | 'email'
  | 'customize-email'
  | 'email-additional-settings'
  | 'link'
  | 'link-additional-settings'
  | 'quick-intake';

type ShareResultsOption = 'yes' | 'no';

export interface Tag {
  value: string;
  isValid: boolean;
  error: string;
  contactId?: string;
  firstName?: string;
  lastName?: string;
  phoneCountryCode?: SupportedCountryCode;
  phoneNumber?: string;
}

export interface InviteClientConfig {
  email: string;
  firstName: string;
  lastName: string;
  birthDate?: Date;
  zipCode: string;
  genderAtBirth: string;
  maritalStatus: string;
  householdSize: string;
  totalAssets: string;
  partnerFirstName: string;
  partnerLastName: string;
  priorYearIncome: string;
  highestDegree: string;
  assetTypes: string[];
  legalDocuments: string[];
  supportNetwork: string[];
  bulkEmails: Tag[];
  isBulkMode: boolean;
  isComplianceConfirmed: boolean;
  phoneCountryCode: SupportedCountryCode;
  phoneNumber: string;
  emailBody: string;
  shareResults: ShareResultsOption;
  referralSource?: string | undefined;
  clientRelationship?: ClientRelationshipStage | undefined;
  redtailContactId?: string | undefined;
  wealthboxContactId?: string | undefined;
}

export interface PrefillDraft {
  partnerFirstName: string;
  partnerLastName: string;
  birthDate?: Date;
  zipCode: string;
  genderAtBirth: string;
  maritalStatus: string;
  householdSize: string;
  totalAssets: string;
  priorYearIncome: string;
  assetTypes: string[];
  legalDocuments: string[];
  supportNetwork: string[];
  highestDegree: string;
}

export interface PrefillDraftState {
  answers: PrefillDraft;
  birthDateInput: string;
  isBirthDateInputValid: boolean;
}

function formatBirthDateInput(date: Date | undefined): string {
  if (!date || Number.isNaN(date.getTime())) return '';

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}/${day}/${date.getFullYear()}`;
}

export function createPrefillDraftState(
  config: Partial<InviteClientConfig> = {},
): PrefillDraftState {
  return {
    answers: {
      partnerFirstName: config.partnerFirstName ?? '',
      partnerLastName: config.partnerLastName ?? '',
      birthDate: config.birthDate,
      zipCode: config.zipCode ?? '',
      genderAtBirth: config.genderAtBirth ?? '',
      maritalStatus: config.maritalStatus ?? '',
      householdSize: config.householdSize ?? '',
      totalAssets: config.totalAssets ?? '',
      priorYearIncome: config.priorYearIncome ?? '',
      assetTypes: [...(config.assetTypes ?? [])],
      legalDocuments: [...(config.legalDocuments ?? [])],
      supportNetwork: [...(config.supportNetwork ?? [])],
      highestDegree: config.highestDegree ?? '',
    },
    birthDateInput: formatBirthDateInput(config.birthDate),
    isBirthDateInputValid: true,
  };
}

export interface CreatedInvite {
  link: string;
  clientName: string;
  hasPrefillPlan: boolean;
}

export interface InviteClientStoreType {
  inviteClientConfig: InviteClientConfig;
  prefillDraftState: PrefillDraftState;
  showEmailWarningModal: boolean;
  showAdditionalSettings: boolean;
  showPrefillAnswers: boolean;
  isEmailValid: boolean;
  isAdvisorEmail: boolean;
  canContinueSingle: boolean;
  validBulkEmails: Tag[];
  canContinueBulk: boolean;
  isLoading: boolean;
  isComplete: boolean;
  isPerClientInviteLink: boolean;
  isConsumerSmsMfaAvailable: boolean;
  advisorPrefillPolicy: AdvisorPrefillPolicy;
  createdInvite: CreatedInvite | null;
  /** The live Fill Intake Form consent request, kept above the tab so Back cannot discard it. */
  agentEnteredIntakeId: string | null;
  dismissCreatedInvite: () => void;

  setInviteClientConfig: Dispatch<SetStateAction<InviteClientConfig>>;
  setPrefillDraftState: Dispatch<SetStateAction<PrefillDraftState>>;
  setShowEmailWarningModal: Dispatch<SetStateAction<boolean>>;
  setShowAdditionalSettings: Dispatch<SetStateAction<boolean>>;
  setShowPrefillAnswers: Dispatch<SetStateAction<boolean>>;
  setAgentEnteredIntakeId: Dispatch<SetStateAction<string | null>>;

  handleConnectEmail: () => void;
  handleClose: () => void;
  generateIntakeFormLink: (surveyId: string, contactId?: string) => string;
  sendInvites: (isBulk: boolean) => void;
  handleSendEmailAnyway: (isBulk: boolean) => void;
  handleSendEmail: (isBulk: boolean) => void;
  generateShareableLink: () => string;
  handleConfigChange: (
    field: keyof InviteClientConfig,
    value: string | boolean | Tag[] | string[] | Date | undefined,
  ) => void;
  handleClearBulkEmails: () => void;
}

export const EMAIL_WARNING_DISMISSED_KEY = 'emailDeliverabilityWarningDismissed';

// Serializes from local date parts (not toISOString) so the calendar day the
// advisor picked is preserved regardless of timezone.
function toIsoDateString(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function buildAdvisorPrefill(
  config: InviteClientConfig,
  policy: AdvisorPrefillPolicy,
): AdvisorPrefillFields {
  const prefill: AdvisorPrefillFields = {};
  const allows = (questionId: string) => isAdvisorPrefillQuestionAllowed(policy, questionId);

  if (allows('clientBirthDate') && config.birthDate) {
    prefill.clientBirthDate = toIsoDateString(config.birthDate);
  }
  if (allows('clientZipCode') && config.zipCode.trim()) {
    prefill.clientZipCode = config.zipCode.trim();
  }
  if (allows('clientGenderAtBirth') && config.genderAtBirth) {
    prefill.clientGenderAtBirth = config.genderAtBirth;
  }
  if (allows('clientMaritalStatus') && config.maritalStatus) {
    prefill.clientMaritalStatus = config.maritalStatus;
  }

  const householdSize = Number(config.householdSize.trim());
  if (
    allows('householdHouseholdSize') &&
    config.householdSize.trim() &&
    Number.isFinite(householdSize)
  ) {
    prefill.householdHouseholdSize = householdSize;
  }

  const totalAssets = parseCurrencyInput(config.totalAssets);
  if (
    allows('householdTotalAssetsValue') &&
    config.totalAssets.trim() &&
    Number.isFinite(totalAssets)
  ) {
    prefill.householdTotalAssetsValue = totalAssets;
  }

  if (allows('partnerFirstName') && config.partnerFirstName.trim()) {
    prefill.partnerFirstName = config.partnerFirstName.trim();
  }
  if (allows('partnerLastName') && config.partnerLastName.trim()) {
    prefill.partnerLastName = config.partnerLastName.trim();
  }

  const priorYearIncome = parseCurrencyInput(config.priorYearIncome);
  if (
    allows('householdPriorYearIncome') &&
    config.priorYearIncome.trim() &&
    Number.isFinite(priorYearIncome)
  ) {
    prefill.householdPriorYearIncome = priorYearIncome;
  }

  if (allows('clientHighestDegree') && config.highestDegree) {
    prefill.clientHighestDegree = config.highestDegree;
  }
  if (allows('financialAssetTypes') && config.assetTypes.length > 0) {
    prefill.financialAssetTypes = config.assetTypes;
  }
  if (allows('legalDocuments') && config.legalDocuments.length > 0) {
    prefill.legalDocuments = config.legalDocuments;
  }
  if (allows('clientProfessionalSupportNetworkList') && config.supportNetwork.length > 0) {
    prefill.clientProfessionalSupportNetworkList = config.supportNetwork;
  }

  return prefill;
}
export const DEFAULT_EMAIL_BODY = `As one of the most devastating events in retirement, it means a lot to me to prepare my clients for the possibility of a long-term care event.

I use Waterlily to build personalized long-term care plans for clients. It works by predicting your unique future LTC needs, building a care plan for you, and then I can analyze different ways we could fund these costs to find the options that make the most sense for you.

The first step is to complete the intake form below (it's about 5 minutes long) and then we get the predictions that we can work with and build from there.`;

export const InviteClientContext = createContext<InviteClientStoreType | null>(null);

export function useInviteClientStore() {
  const context = useContext(InviteClientContext);

  if (!context) {
    throw new Error('`useInviteClientStore` should be used inside the `<InviteClientProvider />`');
  }

  return context;
}

export function useInviteClientTrigger() {
  const [searchParams, setSearchParams] = useSearchParams();

  const isInviteClientOpen = searchParams.get('inviteClient') === 'true';
  const inviteClientTab = searchParams.get('inviteClientType') as TabType;
  const initialBulkEmailsParam = searchParams.get('initialBulkEmails');
  const initialBulkEmails: Tag[] = initialBulkEmailsParam
    ? JSON.parse(decodeURIComponent(initialBulkEmailsParam))
    : [];
  const redtailContactId = searchParams.get('redtailContactId') ?? undefined;
  const wealthboxContactId = searchParams.get('wealthboxContactId') ?? undefined;
  const integrationSource = searchParams.get('integrationSource') as
    | 'redtail'
    | 'wealthbox'
    | undefined;

  const setInviteClientParams = useCallback(
    (
      tab?: TabType,
      bulkEmails?: Tag[],
      contactId?: string,
      integrationSource?: 'redtail' | 'wealthbox',
    ) => {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);

        newParams.set('inviteClient', 'true');
        newParams.set('inviteClientType', tab || 'email');

        if (bulkEmails && bulkEmails.length > 0) {
          newParams.set('initialBulkEmails', encodeURIComponent(JSON.stringify(bulkEmails)));
        }

        if (integrationSource) {
          newParams.set('integrationSource', integrationSource);
        }

        if (contactId) {
          const paramName =
            integrationSource === 'wealthbox' ? 'wealthboxContactId' : 'redtailContactId';
          newParams.set(paramName, contactId);
        }

        return newParams;
      });
    },
    [setSearchParams],
  );

  const clearInviteClientParams = useCallback(() => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      newParams.delete('inviteClient');
      newParams.delete('inviteClientType');
      newParams.delete('initialBulkEmails');
      newParams.delete('redtailContactId');
      newParams.delete('wealthboxContactId');
      newParams.delete('integrationSource');

      return newParams;
    });
  }, [setSearchParams]);

  const setInviteClientTab = useCallback(
    (tab: TabType) => {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('inviteClientType', tab);
        return newParams;
      });
    },
    [setSearchParams],
  );

  return {
    isInviteClientOpen,
    inviteClientTab,
    initialBulkEmails,
    redtailContactId,
    wealthboxContactId,
    integrationSource,
    setInviteClientParams,
    setInviteClientTab,
    clearInviteClientParams,
  };
}

export interface InviteClientProviderProps {
  children: ReactNode;
  onInviteSuccess?: () => void;
  onClose?: () => void;
  prefillWithAdvisor?: boolean;
  skipEmailWarning?: boolean;
}

export function useInviteClientProvider({
  onInviteSuccess,
  onClose,
  prefillWithAdvisor = false,
  skipEmailWarning = false,
}: Omit<InviteClientProviderProps, 'children'>) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const advisor = useSelector(selectSessionAdvisor);
  const advisorFeatureFlags = useSelector(selectFeatureFlags);
  const isEdjPilot = !!advisorFeatureFlags.edjPilotAdvisor;
  const {
    initialBulkEmails,
    redtailContactId,
    wealthboxContactId,
    integrationSource,
    clearInviteClientParams,
  } = useInviteClientTrigger();
  const { flags } = useFeatureFlags();
  const useFormProvider = flags.useFormProvider ?? false;

  const [showEmailWarningModal, setShowEmailWarningModal] = useState(false);
  const [showAdditionalSettings, setShowAdditionalSettings] = useState(false);
  const [showPrefillAnswers, setShowPrefillAnswers] = useState(false);
  const [createdInvite, setCreatedInvite] = useState<CreatedInvite | null>(null);
  const [agentEnteredIntakeId, setAgentEnteredIntakeId] = useState<string | null>(null);
  const advisorPrefillPolicy = useAdvisorPrefillPolicy();
  const isPerClientInviteLink = advisorPrefillPolicy.enabled;
  const isConsumerSmsMfaAvailable = isConsumerSmsMfaEnabled({
    advisorFeatureFlags,
    appConfigFlags: flags,
  });

  const defaultInviteClientConfig: InviteClientConfig = {
    email: '',
    firstName: prefillWithAdvisor ? (advisor.advisorFirstName ?? '') : '',
    lastName: prefillWithAdvisor ? (advisor.advisorLastName ?? '') : '',
    birthDate: undefined,
    zipCode: '',
    genderAtBirth: '',
    maritalStatus: '',
    householdSize: '',
    totalAssets: '',
    partnerFirstName: '',
    partnerLastName: '',
    priorYearIncome: '',
    highestDegree: '',
    assetTypes: [],
    legalDocuments: [],
    supportNetwork: [],
    bulkEmails: [],
    isBulkMode: false,
    isComplianceConfirmed: false,
    phoneCountryCode: '+1',
    phoneNumber: '',
    emailBody: DEFAULT_EMAIL_BODY,
    shareResults: advisor.sendClientResultsLinkSetting ? 'yes' : 'no',
  };

  const [inviteClientConfig, setInviteClientConfig] =
    useState<InviteClientConfig>(defaultInviteClientConfig);
  const singleInviteAttemptId = useRef<string | null>(null);
  const [prefillDraftState, setPrefillDraftState] = useState<PrefillDraftState>(() =>
    createPrefillDraftState(defaultInviteClientConfig),
  );

  useEffect(() => {
    // A retry of the same prefillable-link request keeps its idempotency key. Once the advisor
    // changes any input, the next submission is a new request and must not replay the old link.
    singleInviteAttemptId.current = null;
  }, [inviteClientConfig]);

  useEffect(() => {
    const isSingleFromBulk = initialBulkEmails.length === 1;

    if (isSingleFromBulk) {
      const singleTag = initialBulkEmails[0];
      const contactId = singleTag.contactId;

      setInviteClientConfig(currentConfig => ({
        ...currentConfig,
        email: singleTag.value,
        firstName: singleTag.firstName ?? '',
        lastName: singleTag.lastName ?? '',
        redtailContactId: redtailContactId ? (contactId ?? redtailContactId) : undefined,
        wealthboxContactId: wealthboxContactId ? (contactId ?? wealthboxContactId) : undefined,
      }));
    } else if (initialBulkEmails.length > 1) {
      setInviteClientConfig(currentConfig => ({
        ...currentConfig,
        bulkEmails: initialBulkEmails,
        isBulkMode: true,
        redtailContactId,
        wealthboxContactId,
      }));
    }
  }, [initialBulkEmails, redtailContactId, wealthboxContactId]);

  function fetchClientsForAdvisorOnSuccess() {
    dispatch(
      fetchClientsForAdvisorByAdvisorIdRequest({
        advisorId: advisor.advisorId,
        isInvalidation: true,
      }),
    );
  }

  function resetState() {
    singleInviteAttemptId.current = null;
    setAgentEnteredIntakeId(null);
    setShowPrefillAnswers(false);
    setPrefillDraftState(createPrefillDraftState());
    const isSingleFromBulk = initialBulkEmails.length === 1;

    if (isSingleFromBulk) {
      const singleTag = initialBulkEmails[0];
      const contactId = singleTag.contactId;
      setInviteClientConfig({
        ...defaultInviteClientConfig,
        email: singleTag.value,
        firstName: singleTag.firstName ?? '',
        lastName: singleTag.lastName ?? '',
        redtailContactId: redtailContactId ? (contactId ?? redtailContactId) : undefined,
        wealthboxContactId: wealthboxContactId ? (contactId ?? wealthboxContactId) : undefined,
      });
    } else {
      setInviteClientConfig({
        ...defaultInviteClientConfig,
        bulkEmails: initialBulkEmails.length > 1 ? initialBulkEmails : [],
        isBulkMode: initialBulkEmails.length > 1,
        redtailContactId,
        wealthboxContactId,
      });
    }
    inviteMutation.reset();
    batchInviteMutation.reset();
  }

  const dismissCreatedInvite = useCallback(() => setCreatedInvite(null), []);

  const handleClose = useCallback(() => {
    resetState();
    dispatch({ type: 'RESUME_SESSION_REFRESH' });
    if (onClose) {
      onClose();
    } else {
      clearInviteClientParams();
    }
  }, [onClose, clearInviteClientParams, dispatch]);

  const inviteMutation = useMutation({
    mutationFn: inviteApi.sendInvite,
    onSuccess: data => {
      if (!data.success) {
        toast.error(
          isPerClientInviteLink ? 'Failed to create invite link' : 'Failed to send invite',
          {
            description: data.error || 'Please try again',
          },
        );
        return;
      }

      fetchClientsForAdvisorOnSuccess();

      if (isPerClientInviteLink) {
        singleInviteAttemptId.current = null;
        setCreatedInvite({
          link: data.intakeFormLink ?? '',
          clientName: fullName(
            inviteClientConfig.firstName.trim(),
            inviteClientConfig.lastName.trim(),
          ),
          hasPrefillPlan: !!data.hasPrefillPlan,
        });
        handleClose();
        return;
      }

      toast.success('Invite sent successfully');
      onInviteSuccess ? onInviteSuccess() : handleClose();
    },
    onError: (error: unknown) => {
      toast.error(
        isPerClientInviteLink ? 'Failed to create invite link' : 'Failed to send invite',
        {
          description:
            error instanceof Error ? error.message : 'Please try again or contact support',
        },
      );
    },
  });

  const batchInviteMutation = useMutation({
    mutationFn: inviteApi.sendBatchInvite,
    onSuccess: data => {
      const { errorCount, successCount, errors } = data;
      if (successCount > 0 && errorCount > 0) {
        toast.warning(`Sent ${successCount} invites with ${errorCount} failures`, {
          description: `Failed: ${errors.map(e => e.email).join(', ')}`,
        });
        setInviteClientConfig(currentConfig => ({
          ...currentConfig,
          bulkEmails: currentConfig.bulkEmails.filter(el =>
            errors.map(e => e.email).includes(el.value),
          ),
        }));
        fetchClientsForAdvisorOnSuccess();
        return;
      }
      if (successCount > 0) {
        toast.success(`Successfully sent ${successCount} invites`);
        fetchClientsForAdvisorOnSuccess();
        onInviteSuccess ? onInviteSuccess() : handleClose();
        return;
      }
      toast.error('Failed to send invites', {
        description: 'Please try again or contact support',
      });
    },
    onError: () => {
      toast.error('Failed to send invites', {
        description: 'Please try again or contact support',
      });
    },
  });

  const isEmailValid = isValidEmailFormat(inviteClientConfig.email);
  // Skip advisor self-invite check when FF is on, to allow testing with advisor's own email
  const isAdvisorEmail =
    !flags.advisorClientLogin &&
    advisor.advisorEmail !== undefined &&
    advisor.advisorEmail !== null &&
    inviteClientConfig.email.trim().toLowerCase() === advisor.advisorEmail.trim().toLowerCase();
  const requiresMobileMfa = isConsumerSmsMfaAvailable;
  const isSinglePhoneValid =
    !requiresMobileMfa ||
    validateForCountry(
      stripDigits(inviteClientConfig.phoneNumber),
      inviteClientConfig.phoneCountryCode,
    ) === null;
  const canContinueSingle =
    inviteClientConfig.email.length > 0 && isEmailValid && !isAdvisorEmail && isSinglePhoneValid;
  const validBulkEmails = inviteClientConfig.bulkEmails.filter(
    email =>
      email.isValid &&
      (!requiresMobileMfa ||
        validateForCountry(stripDigits(email.phoneNumber ?? ''), email.phoneCountryCode ?? '+1') ===
          null),
  );
  const canContinueBulk = validBulkEmails.length > 0 && inviteClientConfig.isComplianceConfirmed;

  const isLoading = inviteMutation.isPending || batchInviteMutation.isPending;
  const isComplete = inviteMutation.isSuccess || batchInviteMutation.isSuccess;

  const hasConnectedEmail = !!(
    (advisor.advisorOauthSettings?.google?.isConnected ??
      (advisor.advisorOauthSettings?.google?.userEmail &&
        advisor.advisorOauthSettings?.google?.createdAtDateTime)) ||
    (advisor.advisorOauthSettings?.microsoft?.isConnected ??
      (advisor.advisorOauthSettings?.microsoft?.userEmail &&
        advisor.advisorOauthSettings?.microsoft?.createdAtDateTime))
  );

  function shouldShowEmailDeliverabilityWarning() {
    return localStorage.getItem(EMAIL_WARNING_DISMISSED_KEY) !== 'true';
  }

  useEffect(() => {
    const { firstName } = inviteClientConfig;

    const greeting = firstName ? `Hi ${firstName},\n\n` : '';

    const emailText = `${greeting}As one of the most devastating events in retirement, it means a lot to me to prepare my clients for the possibility of a long-term care event.

I use Waterlily to build personalized long-term care plans for clients. It works by predicting your unique future LTC needs, building a care plan for you, and then I can analyze different ways we could fund these costs to find the options that make the most sense for you.

The first step is to complete the intake form below (it's about 5 minutes long) and then we get the predictions that we can work with and build from there.`;

    setInviteClientConfig(currentConfig => ({
      ...currentConfig,
      emailBody: emailText,
    }));
  }, [inviteClientConfig.firstName]);

  const handleConnectEmail = useCallback(() => {
    setShowEmailWarningModal(false);
    handleClose();
    navigate(`/advisors/${advisor.advisorId}/settings?tab=communication`);
  }, [advisor.advisorId, handleClose, navigate]);

  const generateIntakeFormLink = useCallback(
    (surveyId: string, contactId?: string) => {
      if (useFormProvider) {
        return getClientOnboardingUrl(advisor.advisorId);
      }
      return getIntroIntakeFormApiEndPoint(
        advisor.advisorId,
        inviteClientConfig.shareResults === 'yes',
        surveyId,
        undefined,
        contactId ?? inviteClientConfig.redtailContactId,
        inviteClientConfig.wealthboxContactId,
      );
    },
    [
      advisor.advisorId,
      inviteClientConfig.shareResults,
      inviteClientConfig.redtailContactId,
      inviteClientConfig.wealthboxContactId,
      useFormProvider,
    ],
  );

  const sendInvites = useCallback(
    (isBulk: boolean) => {
      analytics.inviteClients({
        clientCount: isBulk ? validBulkEmails.length : 1,
        inviteMethod: isBulk ? 'bulk' : 'single',
      });

      if (!isBulk) {
        const surveyId = isPerClientInviteLink
          ? (singleInviteAttemptId.current ?? newUuid())
          : newUuid();
        if (isPerClientInviteLink) singleInviteAttemptId.current = surveyId;

        inviteMutation.mutate({
          surveyId,
          createNewClient: true,
          advisorId: advisor.advisorId,
          email: inviteClientConfig.email.trim(),
          clientFirstName: inviteClientConfig.firstName.trim(),
          clientLastName: inviteClientConfig.lastName.trim(),
          advisorPrefill: isPerClientInviteLink
            ? buildAdvisorPrefill(inviteClientConfig, advisorPrefillPolicy)
            : undefined,
          advisorProvidedBody: inviteClientConfig.emailBody,
          intakeFormLink: generateIntakeFormLink(surveyId),
          clientRelationshipStage: inviteClientConfig.clientRelationship,
          referralSourceId: inviteClientConfig.referralSource,
          redtailContactId: inviteClientConfig.redtailContactId,
          wealthboxContactId: inviteClientConfig.wealthboxContactId,
          hasAccessToResults: inviteClientConfig.shareResults === 'yes',
          deliveryMode: isPerClientInviteLink ? 'link' : 'email',
          consumerSmsMfa: requiresMobileMfa
            ? {
                phoneNumber: `${inviteClientConfig.phoneCountryCode}${stripDigits(inviteClientConfig.phoneNumber)}`,
              }
            : undefined,
        });
      } else {
        if (!inviteClientConfig.isComplianceConfirmed) return;

        const payload = validBulkEmails.map(emailTag => {
          const surveyId = newUuid();
          return {
            email: emailTag.value.trim(),
            surveyId,
            advisorId: advisor.advisorId,
            intakeFormLink: generateIntakeFormLink(surveyId, emailTag.contactId),
            clientRelationshipStage: inviteClientConfig.clientRelationship,
            referralSourceId: inviteClientConfig.referralSource,
            advisorProvidedBody: inviteClientConfig.emailBody,
            hasAccessToResults: inviteClientConfig.shareResults === 'yes',
            redtailContactId: integrationSource === 'redtail' ? emailTag.contactId : undefined,
            wealthboxContactId: integrationSource === 'wealthbox' ? emailTag.contactId : undefined,
            clientFirstName: emailTag.firstName?.trim(),
            clientLastName: emailTag.lastName?.trim(),
            consumerSmsMfa: requiresMobileMfa
              ? {
                  phoneNumber: `${emailTag.phoneCountryCode ?? '+1'}${stripDigits(emailTag.phoneNumber ?? '')}`,
                }
              : undefined,
          };
        });

        batchInviteMutation.mutate({ intakeFormEmailProps: payload });
      }
    },
    [
      advisor.advisorId,
      inviteClientConfig,
      generateIntakeFormLink,
      validBulkEmails,
      inviteMutation,
      isPerClientInviteLink,
      advisorPrefillPolicy,
      batchInviteMutation,
      requiresMobileMfa,
    ],
  );

  const handleSendEmail = useCallback(
    (isBulk: boolean) => {
      if (
        !isPerClientInviteLink &&
        !isEdjPilot &&
        !skipEmailWarning &&
        !hasConnectedEmail &&
        shouldShowEmailDeliverabilityWarning()
      ) {
        setShowEmailWarningModal(true);
        return;
      }
      sendInvites(isBulk);
    },
    [isPerClientInviteLink, isEdjPilot, skipEmailWarning, hasConnectedEmail, sendInvites],
  );

  const handleSendEmailAnyway = useCallback(
    (isBulk: boolean) => {
      setShowEmailWarningModal(false);
      sendInvites(isBulk);
    },
    [sendInvites],
  );

  const handleConfigChange = useCallback(
    (
      field: keyof typeof inviteClientConfig,
      value: (typeof inviteClientConfig)[keyof typeof inviteClientConfig],
    ) => {
      setInviteClientConfig(currentConfig => ({ ...currentConfig, [field]: value }));
    },
    [],
  );

  const handleClearBulkEmails = useCallback(() => {
    setInviteClientConfig(currentConfig => ({
      ...currentConfig,
      bulkEmails: [],
      isComplianceConfirmed: false,
    }));
  }, []);

  const generateShareableLink = useCallback(() => {
    if (useFormProvider) {
      return getClientOnboardingUrl(advisor.advisorId, {
        shareResults: inviteClientConfig.shareResults,
        clientRelationshipStage: inviteClientConfig.clientRelationship,
        customReferralSourceId: inviteClientConfig.referralSource,
      });
    }
    return getIntroIntakeFormApiEndPoint(
      advisor.advisorId,
      inviteClientConfig.shareResults === 'yes',
      undefined,
      inviteClientConfig.referralSource,
    );
  }, [
    advisor.advisorId,
    inviteClientConfig.shareResults,
    inviteClientConfig.clientRelationship,
    inviteClientConfig.referralSource,
    useFormProvider,
  ]);

  return {
    inviteClientConfig,
    prefillDraftState,
    showEmailWarningModal,
    showAdditionalSettings,
    showPrefillAnswers,
    isEmailValid,
    isAdvisorEmail,
    canContinueSingle,
    validBulkEmails,
    canContinueBulk,
    isLoading,
    isComplete,
    isPerClientInviteLink,
    isConsumerSmsMfaAvailable,
    advisorPrefillPolicy,
    createdInvite,
    agentEnteredIntakeId,
    dismissCreatedInvite,

    setInviteClientConfig,
    setPrefillDraftState,
    setShowEmailWarningModal,
    setShowAdditionalSettings,
    setShowPrefillAnswers,
    setAgentEnteredIntakeId,

    handleConnectEmail,
    handleClose,
    generateIntakeFormLink,
    sendInvites,
    handleSendEmailAnyway,
    handleSendEmail,
    generateShareableLink,
    handleConfigChange,
    handleClearBulkEmails,
  };
}
